import type { WorldManifest } from "daedalus-core";
import { SIXTEENTHS, type BandMapSize, type CollisionGrid, type Puddle } from "odyssey-shared";
import type { LoadedWorld } from "../../editor/world-loader.ts";
import type { BootedWorld, WorldBoot } from "../world-boot.ts";
import { PageBand } from "./band.ts";
import type { NeighbourGround } from "./ground.ts";
import { CrossingPrep } from "./prep.ts";

/**
 * The stage a crossing leaves, as the crossing reads it: its map and the ground beside it.
 */
export interface LeftStage {
  readonly map: WorldManifest["maps"][number];
  readonly beside: NeighbourGround;
}

/**
 * What the crossing asks of the scene: the stage it stands on, and a change of stage to a booted world in one step
 * (the old one ended, the new one prepared).
 */
export interface CrossingScene {
  readonly stage: () => LeftStage | null;
  readonly restage: (booted: BootedWorld) => void;
}

/**
 * A move to report: what to add to a point of the map left to have it in the new map's frame, in sixteenths.
 */
export interface FrameShift {
  readonly dx: number;
  readonly dy: number;
}

/**
 * The scene's side of a seamless crossing (ADR-147 decisions 1, 6, 8, 9 and 18): the work ahead for neighbours the
 * ground beside the map holds (`CrossingPrep`) and its release when the ground lets one go, the page's band over each
 * stage's grid, the quiet boot of the map a crossing reaches, and the change of stage in one step.
 */
export class CrossingStage {
  public readonly prep: CrossingPrep;
  private booted: BootedWorld | null = null;
  private changing = false;

  private generation = 0;

  /**
   * @param boot The world boot.
   * @param scene The scene's stage and its change.
   * @param prep The work ahead; made over the boot when left out.
   */
  public constructor(
    private readonly boot: WorldBoot,
    private readonly scene: CrossingScene,
    prep?: CrossingPrep,
  ) {
    this.prep = prep ?? new CrossingPrep(boot);
  }

  /**
   * Mounts a stage's band and wires its ground beside the map: each chunk that lands or goes reads the band again and
   * starts the work ahead for a neighbour now held; a neighbour let go releases its work ahead and its fetched chunks.
   *
   * @param booted The booted world the stage stands on.
   * @param beside The stage's ground beside the map.
   * @returns The grid the page predicts on.
   */
  public mount(booted: BootedWorld, beside: NeighbourGround): CollisionGrid {
    const world = booted.world;
    const band = new PageBand(booted.grid, bandSize(booted.map), world.manifest.maps.map(bandSize), world.manifest.seams ?? [], world.manifest.chunkSize);
    beside.onChanged = (id) => {
      band.refresh((neighbour) => beside.collisionOf(neighbour));
      if (beside.holds(id)) {
        void this.prep.ready(id);
      }
    };
    beside.onLetGo = (id) => this.prep.forget(id);
    return band.grid;
  }

  /**
   * The puddles of a stage's map, placed ahead when they were, else placed now, and kept.
   *
   * @param world The loaded world.
   * @param mapId The map's id.
   * @param place Places them now.
   * @returns The puddles.
   */
  public puddles(world: LoadedWorld, mapId: string, place: () => Puddle[]): Puddle[] {
    const list = this.prep.puddlesOf(world.version, mapId) ?? place();
    this.prep.keep(world.version, mapId, list);
    return list;
  }

  /**
   * Boots a neighbour a crossing reaches behind the world shown: the chunks of it the ground beside the map holds are
   * handed to the boot, the work ahead is waited for, and the boot assembles it, with no loading screen. A boot `drop`
   * outdated is not kept.
   *
   * @param mapId The neighbour's id.
   * @throws Error naming what failed to load, as the world boot does.
   */
  public async bootFor(mapId: string): Promise<void> {
    const generation = this.generation;
    const held = this.scene.stage()?.beside.chunksHeld(mapId);
    if (held !== null && held !== undefined) {
      this.boot.adopt(mapId, held);
    }
    await this.prep.ready(mapId);
    const booted = await this.boot.runMap(mapId, true);
    if (generation === this.generation) {
      this.booted = booted;
    }
  }

  /**
   * Whether the stage is changing for a crossing now, for what the new stage gives as a crossing's (its minimap pictures,
   * `MapPictureSource.crossing`).
   *
   * @returns True during `cross`'s change of stage.
   */
  public get restaging(): boolean {
    return this.changing;
  }

  /**
   * Whether the neighbour `bootFor` booted stands ready.
   *
   * @param mapId The neighbour's id.
   * @returns True when it does.
   */
  public ready(mapId: string): boolean {
    return this.booted?.map.id === mapId;
  }

  /**
   * Forgets a booted neighbour, and outdates a boot still running, as a crossing that ends some other way does.
   */
  public drop(): void {
    this.booted = null;
    this.generation += 1;
  }

  /**
   * Stands the scene on the neighbour `bootFor` booted, in one step between two frames: the stage of the map left ends and
   * the new one is prepared, and the map left's chunks are handed to the new ground beside the map, so its ground is drawn
   * on the first frame.
   *
   * @param mapId The neighbour's id.
   * @returns The frame shift, or null when the map is not booted or either map has no place.
   */
  public cross(mapId: string): FrameShift | null {
    const booted = this.booted;
    const left = this.scene.stage();
    const from = left?.map.place;
    const to = booted?.map.place;
    if (booted === null || booted.map.id !== mapId || left === null || from === undefined || to === undefined) {
      return null;
    }
    const leftId = left.map.id;
    const leftChunks = this.boot.chunksOf(leftId);
    this.changing = true;
    try {
      this.scene.restage(booted);
    } finally {
      this.changing = false;
    }
    this.drop();
    if (leftChunks !== null) {
      this.scene.stage()?.beside.seed(leftId, leftChunks);
    }
    return { dx: (from.x - to.x) * SIXTEENTHS, dy: (from.y - to.y) * SIXTEENTHS };
  }
}

/**
 * A manifest map as the hand band reads it (`bandLinks`): its id, place, size and whether it is blank.
 *
 * @param map The map.
 * @returns Its size.
 */
export function bandSize(map: WorldManifest["maps"][number]): BandMapSize {
  return { id: map.id, place: map.place, width: map.width, height: map.height, blank: map.blank === true };
}
